import { useViewportVisibility } from '@/hooks/use-viewport-visibility';
import type { TokenHighlightController } from '@/lib/audio/token-highlight-controller';
import type { TranscriptionToken } from '@/lib/transcription/types';
import type { RefObject } from 'react';
import { useLayoutEffect, useMemo, useState } from 'react';
import { TokenRenderer } from './token-renderer';

interface VirtualizedTokenRendererProps {
  tokens: TranscriptionToken[];
  highlightController: TokenHighlightController;
  segmentBreakIndices?: Set<number>;
  chunkSize?: number;
  className?: string;
  rootRef?: RefObject<Element | null>;
  debug?: boolean;
}

interface TokenChunk {
  id: number;
  startIndex: number;
  tokens: TranscriptionToken[];
  text: string;
}

export const VirtualizedTokenRenderer = ({
  tokens,
  highlightController,
  segmentBreakIndices,
  chunkSize = 220,
  className = '',
  rootRef,
  debug = false,
}: VirtualizedTokenRendererProps) => {
  const chunks = useMemo<TokenChunk[]>(() => {
    if (tokens.length === 0) return [];

    const result: TokenChunk[] = [];
    let chunkId = 0;

    for (let start = 0; start < tokens.length; start += chunkSize) {
      const end = Math.min(start + chunkSize, tokens.length);
      const slice = tokens.slice(start, end);
      const text = slice.map((token) => token.text).join('');

      result.push({
        id: chunkId,
        startIndex: start,
        tokens: slice,
        text,
      });

      chunkId += 1;
    }

    return result;
  }, [tokens, chunkSize]);

  const [visibleChunkIds, setVisibleChunkIds] = useState<Set<number>>(
    new Set(),
  );
  const [rootElement, setRootElement] = useState<Element | null>(null);

  useLayoutEffect(() => {
    if (!rootRef?.current) {
      setRootElement(null);
      return;
    }

    const element = rootRef.current;
    const isScrollable = element.scrollHeight > element.clientHeight + 1;
    setRootElement(isScrollable ? element : null);
  }, [rootRef]);

  const containerRef = useViewportVisibility<HTMLDivElement>({
    elementSelector: '[data-chunk-id]',
    idAttribute: 'data-chunk-id',
    rootMargin: '200px 0px 200px 0px',
    root: rootElement,
    onVisibilityChange: (ids) => setVisibleChunkIds(ids),
    dependencies: [chunks.length],
  });

  return (
    <div ref={containerRef} className={className}>
      {debug && (
        <div className="sticky top-0 z-10 mb-2 flex items-center gap-2 text-[10px] tracking-wide text-gray-500 uppercase">
          <span className="rounded bg-gray-200 px-1.5 py-0.5">
            chunks {chunks.length}
          </span>
          <span className="rounded bg-gray-200 px-1.5 py-0.5">
            visible {visibleChunkIds.size}
          </span>
          <span className="rounded bg-gray-200 px-1.5 py-0.5">
            root {rootElement ? 'container' : 'viewport'}
          </span>
        </div>
      )}
      {chunks.map((chunk) => {
        const isVisible = visibleChunkIds.has(chunk.id);
        const chunkEnd = chunk.startIndex + chunk.tokens.length - 1;
        return (
          <span
            key={chunk.id}
            data-chunk-id={chunk.id}
            className={[
              'relative inline',
              debug
                ? isVisible
                  ? 'outline-1 outline-green-400/60 outline-dashed'
                  : 'outline-1 outline-gray-300/60 outline-dashed'
                : '',
            ].join(' ')}>
            {debug && (
              <span
                className={[
                  'absolute -top-4 left-0 z-10 rounded px-1.5 py-0.5 text-[10px] font-medium',
                  isVisible
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-200 text-gray-600',
                ].join(' ')}>
                #{chunk.id} {chunk.startIndex}-{chunkEnd}
              </span>
            )}
            {isVisible ? (
              <TokenRenderer
                tokens={chunk.tokens}
                highlightController={highlightController}
                segmentBreakIndices={segmentBreakIndices}
                startIndex={chunk.startIndex}
                as="span"
              />
            ) : (
              chunk.text
            )}
          </span>
        );
      })}
    </div>
  );
};
